import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import {
  Badge, EmptyState, PageLoading, SearchInput, Select,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type { Area } from '../../types';

/**
 * Area → distributor assignment page.
 *
 * Shows every active area alongside its current distributor (if any).
 * An admin can assign or change the distributor for each area from a
 * dropdown. This is the bulk-management view — the same assignment
 * can also be made per-distributor in the DistributorDetailDialog.
 */

interface AreaWithDistributor extends Area {
  distributor?: { id: string; name: string } | null;
}

interface DistributorOption {
  id: string;
  fullName: string;
}

export default function DistributorAreasPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [areas, setAreas] = useState<AreaWithDistributor[] | null>(null);
  const [distributors, setDistributors] = useState<DistributorOption[] | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [areaList, distList] = await Promise.all([
        api.get<AreaWithDistributor[]>('/customers/routes'),
        api.get<{ distributors: DistributorOption[] }>('/distributors', { status: 'active' }),
      ]);
      setAreas(areaList.filter((a) => a.status === 'active'));
      setDistributors(distList.distributors);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load data.');
      setAreas([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (areas ?? [])
      .filter((area) => {
        if (filter === 'assigned') return !!area.distributor;
        if (filter === 'unassigned') return !area.distributor;
        return true;
      })
      .filter((area) => !needle
        || area.name.toLowerCase().includes(needle)
        || area.code?.toLowerCase().includes(needle)
        || area.city?.toLowerCase().includes(needle)
        || area.distributor?.name.toLowerCase().includes(needle));
  }, [areas, search, filter]);

  const assignedCount = (areas ?? []).filter((a) => a.distributor).length;

  async function assign(routeId: string, distributorUserId: string | '') {
    setBusy(routeId);
    try {
      // Use the area-side shortcut: PUT /distributors/route/:routeId
      await api.put(`/distributors/route/${routeId}`, {
        distributorId: distributorUserId || null,
      });
      toast.success(distributorUserId ? 'Distributor assigned to area.' : 'Distributor removed from area.');
      void load();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not change assignment.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Area assignments"
        subtitle={areas
          ? `${assignedCount} of ${areas.length} area${areas.length === 1 ? '' : 's'} assigned`
          : undefined}
      />

      <div className="page">
        <div className="toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search areas or distributors" />
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            aria-label="Filter"
          >
            <option value="all">All areas</option>
            <option value="assigned">Has distributor</option>
            <option value="unassigned">No distributor</option>
          </Select>
        </div>

        <div className="card">
          {!areas ? (
            <div className="card-pad"><PageLoading rows={4} /></div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon="map"
              title={search || filter !== 'all' ? 'No areas match that' : 'No active areas'}
              body={search || filter !== 'all'
                ? 'Try a different search or filter.'
                : 'Create areas in the Areas page first, then assign distributors here.'}
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Area</th>
                    <th>Code</th>
                    <th>City</th>
                    <th className="num">Customers</th>
                    <th style={{ minWidth: 200 }}>Distributor</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((area) => (
                    <tr key={area.id}>
                      <td className="primary">{area.name}</td>
                      <td className="mono">{area.code ?? '—'}</td>
                      <td>{area.city ?? '—'}</td>
                      <td className="num">{area.customerCount ?? 0}</td>
                      <td>
                        {isAdmin && distributors ? (
                          <select
                            className="input"
                            value={area.distributor?.id ?? ''}
                            disabled={busy === area.id}
                            onChange={(e) => void assign(area.id, e.target.value)}
                            style={{ maxWidth: 240 }}
                          >
                            <option value="">None</option>
                            {distributors.map((d) => (
                              <option key={d.id} value={d.id}>{d.fullName}</option>
                            ))}
                          </select>
                        ) : (
                          area.distributor
                            ? <Badge tone="neutral">{area.distributor.name}</Badge>
                            : <span className="faint">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
